import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { PORTAL_RELATIVE_URL, ROUTES } from '../router';
import { useLoading } from './LoadingProvider';
import Parse from 'parse';
import { StrokeWearRoles } from '../types/cloud';
import { LoggingContext, useLogging } from './LoggingProvider';
import {
  SEARCH_PARAM_FROM_PATH,
  SEARCH_PARAM_TIMESTAMP,
} from '../pages/unauthorized/TooManyRequests/TooManyRequests';
import useInactivityDetection from '../hooks/useInactivityDetection';
import useAuthSessionDetection from '../hooks/useAuthSessionDetection';

interface AuthContextContent {
  user: Parse.User | undefined;
  login: (data: Parse.User<Parse.Attributes>) => Promise<void>;
  logout: () => Promise<void>;
  devLogin: (username: string, password: string) => Promise<void>;
  roles: StrokeWearRoles[] | undefined;
  getRedirectUri: (provider: 'google' | 'microsoft') => string;
}

const AuthContext = createContext<AuthContextContent>(undefined!);

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<Parse.User | undefined>(
    Parse.User.current() ?? undefined,
  );
  const navigate = useNavigate();
  const { setLoaderDemensions } = useLoading();

  const [roles, setRoles] = useState<StrokeWearRoles[]>();

  const { setUser: setLoggingUser, setRoles: setLoggingRoles } =
    useContext(LoggingContext);
  const logger = useLogging(AuthProvider.name);

  const getUserRoles = useCallback(async () => {
    try {
      const roles = await Parse.Cloud.run('GetRolesForUser');
      setRoles(roles);

      return roles;
    } catch (e) {
      logger.error('Failed to get user roles', e);
      if (
        e instanceof Parse.Error &&
        e.code === Parse.Error.CONNECTION_FAILED &&
        window.location.pathname !==
          PORTAL_RELATIVE_URL + ROUTES.ERROR_TOO_MANY_REQUESTS.path
      ) {
        logger.log('Too many requests error: navigating to 429 page');
        navigate({
          pathname: ROUTES.ERROR_TOO_MANY_REQUESTS.path,
          search: createSearchParams([
            [SEARCH_PARAM_FROM_PATH, window.location.pathname],
            [SEARCH_PARAM_TIMESTAMP, Date.now().toString()],
          ]).toString(),
        });
      }
    }
  }, [logger, navigate]);

  useEffect(() => {
    // set the loader demensions for the  main element
    setLoaderDemensions({ width: '95%', height: '92%' });
    if (user) {
      setLoggingUser(user);
      getUserRoles().then((roles: StrokeWearRoles[]) => setLoggingRoles(roles));
    }
  }, [
    getUserRoles,
    setLoaderDemensions,
    setLoggingRoles,
    setLoggingUser,
    user,
  ]);

  const logout = useCallback(async () => {
    try {
      await Parse.User.logOut();
    } catch (e) {
      // if the session token is invalid, the user is already logged out
      if (
        !(
          e instanceof Parse.Error &&
          e.code === Parse.Error.INVALID_SESSION_TOKEN
        )
      ) {
        // if not, log the error
        logger.warn('Unable to log out', e);
      }
    }
    setUser(undefined);
  }, [logger]);

  const { renderInactivityWarning } = useInactivityDetection({
    user: user ?? null,
    logout,
  });

  const { renderSessionWarning } = useAuthSessionDetection({
    user: user ?? null,
    logout,
  });

  const login = useCallback(
    async (data: Parse.User) => {
      try {
        setUser(data);
        navigate(ROUTES.HOME.path);
      } catch (error) {
        logger.error('Error logging in', error);
      }
    },
    [logger, navigate],
  );

  const devLogin = useCallback(
    async (username: string, password: string) => {
      try {
        const userSignedIn = await Parse.User.logIn(username, password);
        await login(userSignedIn);
      } catch (e) {
        logger.error('unable to log in', username, e);
      }
    },
    [logger, login],
  );

  // if the user is not logged in, redirect to the login page
  useEffect(() => {
    if (!user) {
      navigate(ROUTES.LOGIN.path);
      // set the loader demensions for the login page
      setLoaderDemensions({ width: '100%', height: '100%' });
    }
  }, [navigate, setLoaderDemensions, user]);

  const getRedirectUri = useCallback((provider: 'google' | 'microsoft') => {
    const path =
      provider === 'google'
        ? ROUTES.OAUTH_GOOGLE_REDIRECT.path
        : ROUTES.OAUTH_MICROSOFT_REDIRECT.path;
    return `${window.location.protocol}//${window.location.host}${PORTAL_RELATIVE_URL}${path}`;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        devLogin,
        roles,
        getRedirectUri,
      }}>
      {children}
      {renderInactivityWarning}
      {renderSessionWarning}
    </AuthContext.Provider>
  );
};
const useAuth = () => {
  return useContext(AuthContext);
};

export { useAuth, AuthProvider };
